import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Modal,
  Select,
  Button,
  Row,
  Col,
  Alert,
  Typography,
  message,
  Divider,
  Tag,
} from "antd";
import {
  SwapOutlined,
  BarChartOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { platformApi } from "../../../../api";
import { ContextAPI } from "../../../../ContextAPI";
import ModuleBreakdownPanels from "../ModuleBreakdownPanels";

const { Option } = Select;
const { Text, Title } = Typography;

// ── Profile completeness scorer ───────────────────────────────────────────────
// These field names match what mapCustomer() returns in customer.service.js
const PROFILE_SCALAR_FIELDS = [
  "name", "salutation", "email", "dateOfBirth", "gender",
  "GSTNo", "GSTType", "fatherName", "customerGrouping",
  "customerType", "aadhaarNo", "panCardNo", "refferedBy",
];

const scoreProfile = (cust) => {
  if (!cust) return 0;
  let score = PROFILE_SCALAR_FIELDS.filter((f) => {
    const v = cust[f];
    return v !== null && v !== undefined && v !== "";
  }).length;

  // Address object counts as 1 point if present, +1 more if locality/pincode filled
  if (cust.address) {
    score += 1;
    if (cust.address.locality) score += 1;
    if (cust.address.pincode) score += 1;
    if (cust.address.district?.name) score += 1;
    if (cust.address.state?.name) score += 1;
  }

  return score;
};


// ── Helpers ───────────────────────────────────────────────────────────────────
const getPhone = (cust) =>
  cust?.contacts?.[0]?.phone ||
  cust?.CustomerPhone?.[0]?.phone ||
  cust?.phone ||
  "";

const getLabel = (cust) => {
  if (!cust) return "";
  const phone = getPhone(cust);
  const name = cust.name || "-";
  return phone ? `${name} - ${phone}` : `${name} - No phone`;
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function MergeLedgerModal({ open, prefillDuplicates, close, onMergeSuccess }) {
  const { loginCredintials } = useContext(ContextAPI);

  const [fromCustomer, setFromCustomer] = useState(null);
  const [toCustomer, setToCustomer] = useState(null);
  const [selectedFromId, setSelectedFromId] = useState(undefined);
  const [selectedToId, setSelectedToId] = useState(undefined);
  const [selectedFromLabel, setSelectedFromLabel] = useState(undefined);
  const [selectedToLabel, setSelectedToLabel] = useState(undefined);
  const [fromOptions, setFromOptions] = useState([]);
  const [toOptions, setToOptions] = useState([]);
  const [fromLoading, setFromLoading] = useState(false);
  const [toLoading, setToLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [deletePromptVisible, setDeletePromptVisible] = useState(false);
  const [mergedFromId, setMergedFromId] = useState(null);
  const [mergedFromName, setMergedFromName] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Ref to track when a swap is in progress so the FROM-change useEffect
  // does NOT clear the TO field that was just set by the swap.
  const isSwappingRef = React.useRef(false);

  // ── Suggestions ──────────────────────────────────────────────────────────
  const suggestions = useMemo(() => {
    if (!fromCustomer || !toCustomer) return { profile: null };

    const fromProScore = scoreProfile(fromCustomer);
    const toProScore   = scoreProfile(toCustomer);

    const fromLabel = getLabel(fromCustomer);
    const toLabel   = getLabel(toCustomer);

    // ── Suggestion: Profile Wise ──
    const FIELD_LABELS = {
      salutation: "Salutation", name: "Name", email: "Email",
      dateOfBirth: "DOB", gender: "Gender", GSTNo: "GST No",
      GSTType: "GST Type", fatherName: "Father Name",
      customerGrouping: "Grouping", customerType: "Customer Type",
      aadhaarNo: "Aadhaar", panCardNo: "PAN", refferedBy: "Referred By",
    };

    const getFilledLabels = (cust) => {
      const fields = PROFILE_SCALAR_FIELDS
        .filter((f) => { const v = cust[f]; return v !== null && v !== undefined && v !== ""; })
        .map((f) => FIELD_LABELS[f] || f);
      if (cust.address) fields.push("Address");
      return fields.slice(0, 4);
    };

    let profileSug;
    if (fromProScore === toProScore) {
      profileSug = {
        betterLabel: null,
        betterScore: fromProScore,
        exampleFields: [],
        needsSwap: false,
        equal: true,
      };
    } else {
      const betterCust   = fromProScore > toProScore ? fromCustomer : toCustomer;
      const betterScore  = fromProScore > toProScore ? fromProScore : toProScore;
      const betterLabel  = fromProScore > toProScore ? fromLabel : toLabel;
      const needsSwap    = fromProScore > toProScore; // swap only needed if FROM is better
      profileSug = {
        betterLabel,
        betterScore,
        exampleFields: getFilledLabels(betterCust),
        needsSwap,
        equal: false,
      };
    }

    return { profile: profileSug };
  }, [fromCustomer, toCustomer]);

  // ── Branch helper ─────────────────────────────────────────────────────────
  const getBranchId = () => {
    if (typeof loginCredintials?.branch === "string") return loginCredintials.branch;
    if (loginCredintials?.branch?.id) return loginCredintials.branch.id;
    if (Array.isArray(loginCredintials?.branch)) {
      const b = loginCredintials.branch[0];
      if (typeof b === "string") return b;
      if (b?.id) return b.id;
    }
    try {
      const storedBranch = localStorage.getItem("branch");
      if (storedBranch) {
        const parsed = JSON.parse(storedBranch);
        if (typeof parsed === "string") return parsed;
        if (Array.isArray(parsed)) {
          const b = parsed[0];
          if (typeof b === "string") return b;
          if (b?.id) return b.id;
        }
        if (parsed?.id) return parsed.id;
      }
    } catch (e) {
      console.error("Error reading branch from localStorage", e);
    }
    return undefined;
  };

  // ── Load on open ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      if (prefillDuplicates && prefillDuplicates.length > 0) {
        setFromOptions(prefillDuplicates);
        setToOptions(prefillDuplicates);
        
        // Auto-select the first two duplicates to start loading their details immediately
        if (prefillDuplicates[0]) {
          const c1 = prefillDuplicates[0];
          setSelectedFromId(c1.id);
          setSelectedFromLabel(getLabel(c1));
          fetchCustomerDetails(c1.id, setFromCustomer);
        }
        if (prefillDuplicates[1]) {
          const c2 = prefillDuplicates[1];
          setSelectedToId(c2.id);
          setSelectedToLabel(getLabel(c2));
          fetchCustomerDetails(c2.id, setToCustomer);
        }
      } else {
        fetchInitialCustomers();
      }
    }
  }, [open, loginCredintials, prefillDuplicates]);

  const fetchInitialCustomers = () => {
    setFromLoading(true);
    setToLoading(true);
    const branchId = getBranchId();
    platformApi
      .post("/api/customer/get", { page: 1, size: 100, branch: branchId, forMerge: true })
      .then((res) => {
        setFromLoading(false);
        setToLoading(false);
        if (res.data?.code === 200) {
          const list = res.data.response?.data?.customer || [];
          setFromOptions(list);
          setToOptions(list);
        }
      })
      .catch(() => {
        setFromLoading(false);
        setToLoading(false);
      });
  };

  const searchCustomers = (value, setter, loadingSetter) => {
    if (prefillDuplicates && prefillDuplicates.length > 0) {
      // If we are in duplicate suggestion mode, restrict search to the duplicates
      const lowerVal = (value || "").toLowerCase();
      const filtered = prefillDuplicates.filter(c => 
        (c.name || "").toLowerCase().includes(lowerVal) || 
        getPhone(c).includes(lowerVal)
      );
      setter(filtered);
      return;
    }
    loadingSetter(true);
    const branchId = getBranchId();
    platformApi
      .post("/api/customer/get", {
        page: 1,
        size: 100,
        searchString: (value || "").trim(),
        branch: branchId,
        forMerge: true,
      })
      .then((res) => {
        loadingSetter(false);
        if (res.data?.code === 200) {
          setter(res.data.response?.data?.customer || []);
        }
      })
      .catch(() => loadingSetter(false));
  };

  const fetchCustomerDetails = (id, setterFn) => {
    // Step 1: Get customer details first to extract phone & name for further searches
    platformApi.get(`/api/customer/details/${id}`).then((detailsRes) => {
      if (detailsRes.data.code !== 200) return;

      const raw  = detailsRes.data.response;
      const data = raw?.data || raw || {};
      const cust = data.customer || data;

      // Extract phone and name for filtering other APIs
      const phone = cust.contacts?.[0]?.phone || cust.CustomerPhone?.[0]?.phone || "";
      const name  = cust.name || "";
      const searchKey = phone || name; // prefer phone for precise match

      const branchId = getBranchId();
      const branchArr = branchId ? [branchId] : [];

      // Step 2: Fetch all module APIs in parallel using phone/name as searchString
      Promise.all([
        // Bookings by customer phone
        platformApi.post("/api/booking/get", {
          page: 1, size: 500, searchString: searchKey, branch: branchArr,
        }).catch(() => null),
        // Vehicle Sale Invoices by customer phone/name
        platformApi.post("/api/saleInvoice/get", {
          page: 1, size: 500, searchString: name || searchKey,
        }).catch(() => null),
        // Spare Sale Invoices + Counter Sales by customer phone/name
        platformApi.post("/api/saleSpareInvoice/get", {
          page: 1, size: 500, searchString: name || searchKey,
        }).catch(() => null),
        // Receipts/Payments by customer name (payment/getAll matches customerName column)
        platformApi.post("/api/payment/getAll", {
          page: 1, size: 500, searchString: name || searchKey, branchId: branchArr,
        }).catch(() => null),
        // Estimates by customer phone/name
        platformApi.post("/api/estimate/get", {
          page: 1, size: 500, searchString: searchKey, branch: branchArr,
        }).catch(() => null),
      ]).then(([bookingRes, saleInvRes, spareInvRes, payRes, estRes]) => {
        // Bookings
        const bookings = bookingRes?.data?.response?.data?.booking || [];

        // Job orders & embedded sub-modules (Estimates & Spare Orders/Parts Issue mapped with Job Card)
        const jobOrdersList = data.jobOrders || cust.jobOrders || [];

        // Extract estimates mapped directly with Job Cards
        const jobOrderEstimates = jobOrdersList.flatMap((j) => {
          const list = [];
          if (j.Estimate) list.push(j.Estimate);
          if (j.estimates && Array.isArray(j.estimates)) list.push(...j.estimates);
          else if (j.estimate) list.push(j.estimate);
          return list;
        });
        const apiEstimates = estRes?.data?.response?.data?.Estimate || [];
        const estIds = new Set(jobOrderEstimates.map((e) => e.id || e.estimateNo).filter(Boolean));
        const extraEstimates = apiEstimates.filter((e) => !estIds.has(e.id || e.estimateNo));
        const estimates = [...jobOrderEstimates, ...extraEstimates];

        // Derive payments nested in bookings + spare orders + job orders
        const baseSpareOrders = data.spareOrders || cust.spareOrders || [];
        const jobOrderSpareOrders = jobOrdersList.flatMap((j) => {
          const list = [];
          if (j.spareOrders && Array.isArray(j.spareOrders)) list.push(...j.spareOrders);
          else if (j.spareOrder) list.push(j.spareOrder);
          if (j.customerSpareOrder) list.push(j.customerSpareOrder);
          return list;
        });
        const spareOrderIds = new Set(baseSpareOrders.map((s) => s.id || s.orderNumber).filter(Boolean));
        const extraSpareOrders = jobOrderSpareOrders.filter((s) => !spareOrderIds.has(s.id || s.orderNumber));
        const spareOrders = [...baseSpareOrders, ...extraSpareOrders];

        const bookingPayments    = bookings.flatMap((b) => (b.payments || []).map((p) => ({ ...p, bookingId: b.bookingId || b.id })));
        const spareOrderPayments = spareOrders.flatMap((s) => (s.payments || []));
        const jobOrderPayments   = jobOrdersList.flatMap((j) => (j.payments || j.receipts || []));

        // Receipts from payment/getAll — filter to this customer only
        const allPayments = payRes?.data?.response?.data?.payments || [];
        const customerPayments = allPayments.filter((p) => {
          const pCustName = (p.customerName || p.paidBy?.name || "").toLowerCase().trim();
          const targetName = name.toLowerCase().trim();
          const firstName = targetName.split(" ")[0];

          const matchName = targetName && (
            pCustName.includes(targetName) ||
            targetName.includes(pCustName) ||
            (firstName.length > 2 && pCustName.includes(firstName))
          );
          const matchPhone = phone && (
            (p.phone && String(p.phone).includes(phone)) ||
            (p.customerPhone && String(p.customerPhone).includes(phone))
          );
          const matchCustId = (p.customerId === id) || (p.customer?.id === id);
          const matchBooking = p.bookingId && bookings.some((b) => b.bookingId === p.bookingId || b.id === p.bookingId);
          return matchName || matchPhone || matchCustId || matchBooking;
        });

        // Merge: start with nested payments, add any from getAll not already included
        const nestedPaymentIds = new Set(
          [...bookingPayments, ...spareOrderPayments, ...jobOrderPayments]
            .map((p) => p.id || p._id || p.receiptId)
            .filter(Boolean)
        );
        const extraPayments = customerPayments.filter(
          (p) => !nestedPaymentIds.has(p.id || p._id || p.receiptId)
        );
        const payments = [...bookingPayments, ...spareOrderPayments, ...jobOrderPayments, ...extraPayments];

        // Sale invoices — filter by customer phone/name
        const allSaleInv = saleInvRes?.data?.response?.data?.SaleInvoice || [];
        const saleInvoices = allSaleInv.filter((inv) =>
          (inv.customer?.id === id) ||
          (inv.customerName && inv.customerName.toLowerCase().includes(name.toLowerCase())) ||
          (inv.customer?.contacts?.[0]?.phone === phone)
        );

        // Counter Sales (Spare Sale Invoices) — filter by customer phone/name
        const allSpareInv = spareInvRes?.data?.response?.data?.saleSpareInvoice || [];
        const customerSpareInvoices = allSpareInv.filter((inv) => {
          const invPartyName = (inv.partyName?.name || inv.customerName || inv.customer?.name || "").toLowerCase().trim();
          const targetName = name.toLowerCase().trim();
          const firstName = targetName.split(" ")[0];

          const matchName = targetName && (
            invPartyName.includes(targetName) ||
            targetName.includes(invPartyName) ||
            (firstName.length > 2 && invPartyName.includes(firstName))
          );
          const matchPhone = phone && (
            (inv.partyName?.contacts?.[0]?.phone === phone) ||
            (inv.customerPhone && String(inv.customerPhone).includes(phone))
          );
          const matchCustId = (inv.partyName?.id === id) || (inv.customer?.id === id) || (inv.partyId === id);
          return matchName || matchPhone || matchCustId;
        });

        // Also pick up any spareOrders with orderType === "COUNTER_SALE"
        const counterSaleOrders = spareOrders.filter(
          (s) => s.orderType === "COUNTER_SALE" || s.orderType === "COUNTER"
        );
        const counterSaleIds = new Set(customerSpareInvoices.map((inv) => inv.id || inv.invoiceNumber).filter(Boolean));
        const extraCounterSales = counterSaleOrders.filter((s) => !counterSaleIds.has(s.id || s.orderNumber));
        const saleSpareInvoices = [...customerSpareInvoices, ...extraCounterSales];

        const merged = {
          ...cust,
          purchasedVehicle: data.purchasedVehicle || cust.purchasedVehicle || [],
          jobOrders:        jobOrdersList,
          spareOrders,
          estimates,
          booking:          bookings,
          quotation:        data.quotation        || cust.quotation        || [],
          saleInvoices,
          saleSpareInvoices,
          payments,
        };
        setterFn(merged);
      });
    });
  };

  // ── Swap ──────────────────────────────────────────────────────────────────
  const handleSwap = () => {
    // Signal to the fromCustomer useEffect that a swap is in progress
    // so it skips clearing the TO field.
    isSwappingRef.current = true;

    const [tC, tId, tLabel, tOpts] = [toCustomer, selectedToId, selectedToLabel, toOptions];
    setToCustomer(fromCustomer);
    setSelectedToId(selectedFromId);
    setSelectedToLabel(selectedFromLabel);
    setToOptions(fromOptions);
    setFromCustomer(tC);
    setSelectedFromId(tId);
    setSelectedFromLabel(tLabel);
    setFromOptions(tOpts);
  };

  // ── TO dropdown filtering ──────────────────────────────────────────────────
  // Always exclude FROM record. Phone-based pre-filtering is only for the
  // initial default view (set via useEffect below). Searches are unrestricted.
  const filteredToOptions = useMemo(() => {
    const fromId = fromCustomer?.id;
    return toOptions.filter((c) => c.id !== fromId);
  }, [toOptions, fromCustomer]);

  // When FROM is selected: pre-populate TO with same-phone records (Case 2).
  // Skip clearing TO if this change was caused by a swap (isSwappingRef).
  useEffect(() => {
    if (!fromCustomer) return;

    // If swap just happened, skip the TO-clear and pre-load, then reset the flag.
    if (isSwappingRef.current) {
      isSwappingRef.current = false;
      return;
    }

    if (prefillDuplicates && prefillDuplicates.length > 0) {
      // If using suggestions, we don't need to fetch toOptions again
      // We also shouldn't clear TO if it's already set (like on initial open)
      if (selectedToId && prefillDuplicates.some(c => c.id === selectedToId)) {
        return; // TO is already valid
      }
    } else {
      const fromPhone = getPhone(fromCustomer);
      if (fromPhone) {
        setToLoading(true);
        const branchId = getBranchId();
        platformApi
          .post("/api/customer/get", {
            page: 1,
            size: 100,
            searchString: fromPhone,
            branch: branchId,
            forMerge: true,
          })
          .then((res) => {
            setToLoading(false);
            if (res.data?.code === 200) {
              setToOptions(res.data.response?.data?.customer || []);
            }
          })
          .catch(() => setToLoading(false));
      } else {
        fetchInitialCustomers();
      }
    }
    
    // Clear TO only when FROM is manually changed (not during swap or prefill)
    setSelectedToId(undefined);
    setSelectedToLabel(undefined);
    setToCustomer(null);
  }, [fromCustomer?.id, prefillDuplicates]);

  // Helper: render FROM options (no filter)
  const renderFromOptions = () => {
    const list = [...fromOptions];
    if (fromCustomer && !list.some((c) => c.id === fromCustomer.id)) {
      list.unshift(fromCustomer);
    }
    return list.map((c) => {
      const phone = c.contacts?.[0]?.phone || c.CustomerPhone?.[0]?.phone || c.phone || "";
      const display =
        selectedFromId === c.id && selectedFromLabel
          ? selectedFromLabel
          : `${c.name || "-"} - ${phone || "No phone"}`;
      return (
        <Option key={c.id} value={c.id}>
          {display}
        </Option>
      );
    });
  };

  // Helper: render filtered TO options
  const renderToOptions = () => {
    const list = [...filteredToOptions];
    if (toCustomer && !list.some((c) => c.id === toCustomer.id)) {
      // Keep currently selected TO even if it was filtered (e.g. after swap)
      list.unshift(toCustomer);
    }
    return list.map((c) => {
      const phone = c.contacts?.[0]?.phone || c.CustomerPhone?.[0]?.phone || c.phone || "";
      const display =
        selectedToId === c.id && selectedToLabel
          ? selectedToLabel
          : `${c.name || "-"} - ${phone || "No phone"}`;
      return (
        <Option key={c.id} value={c.id}>
          {display}
        </Option>
      );
    });
  };

  // ── Merge handlers ────────────────────────────────────────────────────────
  const handleMerge = () => {
    if (!fromCustomer || !toCustomer) {
      return message.warning("Please select both Ledger From and Ledger To");
    }
    if (fromCustomer.id === toCustomer.id) {
      return message.error("Ledger From and Ledger To cannot be the same customer");
    }
    setConfirmVisible(true);
  };

  const executeMerge = () => {
    setMerging(true);
    platformApi
      .post("/api/customer/merge-ledger", {
        fromCustomerId: fromCustomer.id,
        toCustomerId: toCustomer.id,
        reason: "User initiated ledger merge from Customer Details",
      })
      .then((res) => {
        setMerging(false);
        setConfirmVisible(false);
        if (res.data.code === 200) {
          const fromId = res.data.response?.data?.fromCustomerId || fromCustomer.id;
          setMergedFromId(fromId);
          setMergedFromName(fromCustomer.name);
          setDeletePromptVisible(true);
        } else {
          message.error("Merge failed. Please try again.");
        }
      })
      .catch(() => {
        setMerging(false);
        message.error("An error occurred during merge.");
      });
  };

  const handleDeleteFrom = () => {
    setDeleting(true);
    platformApi
      .delete(`/api/customer/${mergedFromId}`)
      .then((res) => {
        setDeleting(false);
        setDeletePromptVisible(false);
        if (res.data.code === 200 && res.data.response?.code === 200) {
          message.success(`"${mergedFromName}" has been permanently deleted.`, 4);
        } else {
          message.warning(res.data.response?.message || "Merge succeeded but delete failed due to linked records. Please delete manually.", 6);
        }
        onMergeSuccess && onMergeSuccess();
        handleClose();
      })
      .catch(() => {
        setDeleting(false);
        message.error("Delete failed. Please delete the customer manually.");
        onMergeSuccess && onMergeSuccess();
        handleClose();
      });
  };

  const handleKeepFrom = () => {
    setDeletePromptVisible(false);
    message.success(
      `Merge complete. "${mergedFromName}" profile kept with no module links.`,
      4
    );
    onMergeSuccess && onMergeSuccess();
    handleClose();
  };

  const handleClose = () => {
    setFromCustomer(null);
    setToCustomer(null);
    setSelectedFromId(undefined);
    setSelectedToId(undefined);
    setSelectedFromLabel(undefined);
    setSelectedToLabel(undefined);
    setFromOptions([]);
    setToOptions([]);
    setConfirmVisible(false);
    setDeletePromptVisible(false);
    setMergedFromId(null);
    setMergedFromName("");
    close();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Main Merge Ledger Modal ── */}
      <Modal
        title={<Title level={4}>Merge Ledger</Title>}
        visible={open}
        onCancel={handleClose}
        width={780}
        footer={[
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="merge"
            type="primary"
            disabled={!fromCustomer || !toCustomer}
            onClick={handleMerge}
          >
            Merge Ledger
          </Button>,
        ]}
      >
        {/* Selectors */}
        <Row gutter={16} align="middle">
          <Col span={11}>
            <div>
              <span style={{ color: "red", marginRight: 4 }}>*</span>
              <Text strong>Ledger From</Text>
            </div>
            <Select
              showSearch
              placeholder="Search customer by name or mobile"
              style={{ width: "100%", marginTop: 6 }}
              loading={fromLoading}
              value={selectedFromId}
              filterOption={false}
              onFocus={() => {
                if (fromOptions.length === 0) fetchInitialCustomers();
              }}
              onSearch={(val) => searchCustomers(val, setFromOptions, setFromLoading)}
              onChange={(val) => {
                if (!val) {
                  setSelectedFromId(undefined);
                  setSelectedFromLabel(undefined);
                  setFromCustomer(null);
                  // Reset TO selection if same customer was selected there
                  if (selectedToId === val) {
                    setSelectedToId(undefined);
                    setSelectedToLabel(undefined);
                    setToCustomer(null);
                  }
                } else {
                  setSelectedFromId(val);
                  fetchCustomerDetails(val, setFromCustomer);
                }
              }}
              onSelect={(_, option) => setSelectedFromLabel(option.children)}
              allowClear
            >
              {renderFromOptions()}
            </Select>
          </Col>

          <Col span={2} style={{ textAlign: "center", paddingTop: 24 }}>
            <Button
              shape="circle"
              onClick={handleSwap}
              title="Swap Direction"
              disabled={!fromCustomer || !toCustomer}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                fontSize: 16,
                color: fromCustomer && toCustomer ? "#1890ff" : "#bfbfbf",
              }}
            >
              <SwapOutlined />
            </Button>
          </Col>

          <Col span={11}>
            <div>
              <span style={{ color: "red", marginRight: 4 }}>*</span>
              <Text strong>Ledger To</Text>
            </div>
            <Select
              showSearch
              placeholder={
                fromCustomer
                  ? getPhone(fromCustomer)
                    ? "Showing same-phone records — type to search any customer"
                    : "Search by name or mobile"
                  : "Select Ledger From first"
              }
              style={{ width: "100%", marginTop: 6 }}
              loading={toLoading}
              value={selectedToId}
              filterOption={false}
              disabled={!fromCustomer}
              onFocus={() => {
                if (toOptions.length === 0) fetchInitialCustomers();
              }}
              onSearch={(val) => {
                // Free search — user can find any customer. FROM record excluded via filteredToOptions.
                searchCustomers(val, setToOptions, setToLoading);
              }}
              onChange={(val) => {
                if (!val) {
                  setSelectedToId(undefined);
                  setSelectedToLabel(undefined);
                  setToCustomer(null);
                } else {
                  setSelectedToId(val);
                  fetchCustomerDetails(val, setToCustomer);
                }
              }}
              onSelect={(_, option) => setSelectedToLabel(option.children)}
              allowClear
            >
              {renderToOptions()}
            </Select>
          </Col>
        </Row>

        {/* Smart Suggestions */}
        {suggestions.profile && (
          <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
            {suggestions.profile?.needsSwap && (
              <Alert
                type="warning"
                showIcon
                icon={<UserOutlined />}
                message={
                  <div>
                    <div style={{ marginBottom: 4 }}>
                      <Text strong>Suggestion — Customer Details</Text>
                    </div>
                    <Text>
                      &quot;{suggestions.profile.betterLabel}&quot; has a more complete profile
                      {suggestions.profile.exampleFields.length > 0 && (
                        <span style={{ color: "#595959" }}>
                          {" "}({suggestions.profile.exampleFields.join(", ")}{suggestions.profile.betterScore > 4 ? "…" : ""})
                        </span>
                      )}
                      . Recommend keeping it as <Text strong>Ledger To</Text>.
                    </Text>
                  </div>
                }
                action={
                  <Button size="small" onClick={handleSwap}>
                    ↔ Apply
                  </Button>
                }
              />
            )}
          </div>
        )}
      </Modal>

      {/* ── Confirm Merge Modal ── */}
      <Modal
        title={<Title level={4}>Confirm Merge</Title>}
        visible={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        width={600}
        bodyStyle={{ maxHeight: "65vh", overflowY: "auto", padding: "16px 24px" }}
        footer={[
          <Button key="cancel" onClick={() => setConfirmVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={merging}
            onClick={executeMerge}
          >
            Confirm Merge
          </Button>,
        ]}
      >
        {/* Professional header message */}
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 16 }}
          message={
            <span>
              The following records from{" "}
              <Text strong style={{ color: "#d46b08" }}>
                {getLabel(fromCustomer)}
              </Text>{" "}
              will be permanently transferred to{" "}
              <Text strong style={{ color: "#096dd9" }}>
                {getLabel(toCustomer)}
              </Text>
              . This action is irreversible and will be logged for audit purposes.
            </span>
          }
        />

        {/* Summary tags */}
        <div style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Tag color="red">{getLabel(fromCustomer)} → Source</Tag>
          <Tag color="blue">{getLabel(toCustomer)} → Destination</Tag>
        </div>

        <Divider style={{ margin: "10px 0 14px" }}>Records to be Transferred</Divider>

        {/* Module breakdown using live fromCustomer data */}
        <ModuleBreakdownPanels snapshot={fromCustomer} isLiveData={true} />
      </Modal>

      {/* ── Post-Merge Delete Prompt ── */}
      <Modal
        title={
          <span>
            <DeleteOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
            Delete Source Customer?
          </span>
        }
        visible={deletePromptVisible}
        onCancel={handleKeepFrom}
        width={420}
        footer={[
          <Button key="keep" onClick={handleKeepFrom}>
            Keep Customer
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            loading={deleting}
            onClick={handleDeleteFrom}
          >
            Delete Permanently
          </Button>,
        ]}
      >
        <p style={{ margin: 0 }}>
          Merge is complete. Do you want to permanently delete{" "}
          <Text strong>&quot;{mergedFromName}&quot;</Text>?
        </p>
        <ul style={{ marginTop: 12, paddingLeft: 20, color: "#595959" }}>
          <li>
            <Text strong>Delete Permanently</Text> — Customer record is removed from the
            system. Cannot be undone.
          </li>
          <li style={{ marginTop: 6 }}>
            <Text strong>Keep Customer</Text> — Profile stays in the system (name, phone,
            details intact) but with no module links.
          </li>
        </ul>
      </Modal>
    </>
  );
}
